import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCheck, FiRefreshCw, FiArrowRight } = FiIcons;

const EmailVerificationModal = ({ isOpen, onClose, userEmail, cityName }) => {
  const navigate = useNavigate();
  const { dbOperations, setShowEmailVerification } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Check URL for verification token on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token && isOpen) {
      setVerificationToken(token);
      handleVerifyToken(token);
    }
  }, [isOpen]);

  const handleVerifyToken = async (token) => {
    setIsVerifying(true);
    try {
      const result = await dbOperations.verifyEmail(token);
      
      if (result.valid) {
        // Email verified successfully
        setShowEmailVerification(false);
        onClose();
        
        // Redirect to onboarding
        setTimeout(() => {
          navigate('/onboarding');
        }, 100);
      } else {
        setResendStatus({
          type: 'error',
          message: 'Invalid or expired verification link'
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setResendStatus({
        type: 'error',
        message: 'Verification failed. Please try again.'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendEmail = async () => {
    if (!userEmail) return;

    setIsResending(true);
    setResendStatus(null);

    try {
      const result = await dbOperations.createMagicLink(userEmail, 'verify_email');
      
      if (result.token) {
        setResendStatus({
          type: 'success',
          message: 'Verification email sent! Please check your inbox.'
        });
        
        // For demo purposes, show the verification link
        console.log(`Verification link: ${window.location.origin}/#/?token=${result.token}`);
      } else {
        throw new Error('Failed to create verification link');
      }
    } catch (error) {
      console.error('Error resending email:', error);
      setResendStatus({
        type: 'error',
        message: 'Failed to send email. Please try again later.'
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSkipForNow = () => {
    onClose();
    setShowEmailVerification(false);
    navigate('/onboarding');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full text-center"
      >
        {isVerifying ? (
          // Verification in progress
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              E-Mail wird bestätigt...
            </h2>
            <p className="text-gray-600">
              Bitte warten Sie, während wir Ihre E-Mail-Adresse bestätigen.
            </p>
          </>
        ) : (
          // Email verification required
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiMail} className="text-3xl text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🎉 Account erstellt!
            </h2>
            
            <p className="text-gray-600 mb-6">
              Willkommen bei CityShot, <span className="font-semibold text-blue-600">{cityName}</span>!<br />
              Wir haben eine Bestätigungs-E-Mail an <br />
              <span className="font-semibold text-blue-600">{userEmail}</span> gesendet.
            </p>

            {/* Status Messages */}
            {resendStatus && (
              <div className={`mb-6 p-4 rounded-xl ${
                resendStatus.type === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center justify-center gap-2">
                  <SafeIcon icon={resendStatus.type === 'success' ? FiCheck : FiRefreshCw} />
                  <span className="text-sm">{resendStatus.message}</span>
                </div>
              </div>
            )}

            {/* Manual Token Input for Demo */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-2">Demo: Token manuell eingeben</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verificationToken}
                  onChange={(e) => setVerificationToken(e.target.value)}
                  placeholder="Verification token"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => handleVerifyToken(verificationToken)}
                  disabled={!verificationToken.trim() || isVerifying}
                  className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Bestätigen
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={isResending}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isResending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    E-Mail wird gesendet...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiRefreshCw} />
                    E-Mail erneut senden
                  </>
                )}
              </button>
              
              <button
                onClick={handleSkipForNow}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiArrowRight} />
                Später bestätigen - Jetzt einrichten
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 text-xs text-gray-500 space-y-1">
              <p>E-Mail nicht erhalten?</p>
              <p>• Prüfen Sie Ihren Spam-Ordner</p>
              <p>• Stellen Sie sicher, dass {userEmail} korrekt ist</p>
              <p>• Warten Sie einige Minuten und versuchen Sie es erneut</p>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default EmailVerificationModal;